import { PencilIcon, TrashIcon } from "@heroicons/react/20/solid";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { useState } from "react";
import { generatePath } from "react-router-dom";

import AddressDisplay from "~/components/address/AddressDisplay.tsx";
import BreadcrumbsWrapper from "~/components/BreadcrumbsV2";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import Card from "~/components/Card";
import CardValue from "~/components/Card/components/CardValue.tsx";
import CompanyLogo from "~/components/CompanyLogo";
import ContentWrapper from "~/components/ContentWrapper";
import Headline from "~/components/Headline";
import MainContent from "~/components/mainContent/MainContent";
import Section from "~/components/sections/Section";
import { CLIENT_DETAILS_PATH, CLIENTS_INDEX_PATH, COMPANY_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import UpdateContactPersonSidebar from "~/modules/client/components/contact/components/UpdateContactSidebar";
import DeleteClientContactPersonModal from "~/modules/client/components/DeleteClientContactPersonModal";
import { formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";

interface Props {
	clientContactPersonData: ClientContactPersonType;
	client: Client;
	company: Company;
	additionalData: {
		genderName: string;
		languageDisplayName: string;
		managerFullName: string;
		salesFunnelDisplayName: string;
		salesTypeDisplayName: string;
	};
}

const ContactDetailsView: React.FC<Props> = ({ additionalData, client, clientContactPersonData, company }) => {
	const {
		id,
		city,
		clientId,
		countryId,
		dateOfBirth,
		emailBusiness,
		emailPrivate,
		firstName,
		houseNumber,
		jobTitle,
		lastName,
		mobilePhone,
		nobilityTitle,
		phone,
		street,
		title,
		useInformalSalutation,
		zipCode,
	} = clientContactPersonData;

	const { genderName, languageDisplayName, managerFullName, salesFunnelDisplayName, salesTypeDisplayName } =
		additionalData;

	const [showUpdateForm, setShowUpdateForm] = useState(false);
	const [showDeletionModal, setShowDeletionModal] = useState(false);
	const hasAddress = !!street && !!houseNumber && !!zipCode && !!city && !!countryId;

	return (
		<MainContent>
			<Section className="bg-white">
				<BreadcrumbsWrapper>
					<BreadcrumbElement text="Kunden"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={company.displayName}
									   url={generatePath(COMPANY_DETAILS_PATH, { companyId: company.id })} />
					<BreadcrumbElement text="Firmen"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={client.displayName}
									   url={generatePath(CLIENT_DETAILS_PATH, { clientId: client.id })} />
					<BreadcrumbElement text="Kontakte"
									   url={CLIENTS_INDEX_PATH} />
				</BreadcrumbsWrapper>
			</Section>
			<Section className="bg-white pb-2">
				<div className="flex w-full justify-between py-2">
					<div className="flex items-center gap-x-4">
						{company.avatarImage && <CompanyLogo image={company.avatarImage} />}
						<div className="flex flex-col">
							<Headline type="h2">{title} {nobilityTitle} {firstName} {lastName}</Headline>
							<div className="text-sm text-gray-500">{`${jobTitle ? jobTitle + " | " : ""}${company.corporateName}`}</div>
						</div>
					</div>
					<ButtonWithPopoverDots
						theme="dark"
						items={[
							{
								label: "Bearbeiten",
								onClick: () => setShowUpdateForm(true),
								icon: PencilIcon,
							},
							{
								label: "Löschen",
								onClick: () => setShowDeletionModal(true),
								icon: TrashIcon,
							},
						]}
					/>
				</div>
			</Section>

			<ContentWrapper className="mt-8 grid w-full grid-cols-2 gap-x-6">
				<div className="space-y-4">
					<Card title="Persönliche Daten">
						<div className="grid grid-cols-2 gap-4">
							<CardValue label="Titel">
								{title || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Adelstitel">
								{nobilityTitle || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Geburtstag">
								{dateOfBirth ? formatDateWithGermanMonth(new Date(dateOfBirth)) : EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Geschlecht">
								{genderName || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
						</div>
					</Card>
					<Card title="Sales">
						<div className="grid grid-cols-2 gap-4">
							<CardValue label="Sales Funnel">
								{salesFunnelDisplayName || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Sales Typ">
								{salesTypeDisplayName || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Sprache">
								{languageDisplayName || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Anrede">
								{useInformalSalutation ? "Du" : "Sie"}
							</CardValue>
							<CardValue label="Manager:in">
								{managerFullName || EMPTY_VALUE_PLACEHOLDER }
							</CardValue>
						</div>
					</Card>
				</div>
				<div>
					<Card title="Kontaktdetails">
						<div className="grid grid-cols-2 gap-4">
							<CardValue label="E-Mail geschäftlich"
									   enableCopyToClipboard={!!emailBusiness}>
								{emailBusiness || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="E-Mail privat"
									   enableCopyToClipboard={!!emailPrivate}>
								{emailPrivate || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Telefon"
									   enableCopyToClipboard={!!phone}>
								{phone || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Mobil"
									   enableCopyToClipboard={!!mobilePhone}>
								{mobilePhone || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="Addresse"
									   enableCopyToClipboard={hasAddress}>
								{hasAddress ? <AddressDisplay city={city}
															  countryId={countryId}
															  houseNumber={houseNumber}
															  street={street}
															  zipCode={zipCode} /> : EMPTY_VALUE_PLACEHOLDER}
							</CardValue>

						</div>
					</Card>
				</div>
			</ContentWrapper>


			<UpdateContactPersonSidebar
				isOpen={showUpdateForm}
				setOpen={() => setShowUpdateForm(false)}
				contactPersonData={clientContactPersonData}
			/>

			<DeleteClientContactPersonModal clientId={clientId}
											clientContactPersonId={id}
											isOpen={showDeletionModal}
											onClose={() => setShowDeletionModal(false)} />


		</MainContent>
	);
};

export default ContactDetailsView;
